/**
 * ThemeSwitcher — kompaktní trigger + popup s miniaturami schémat.
 * Popup je portál na document.body (fixed), aby ho overflow sidebaru
 * neořízl a nevykreslil mimo obrazovku (patička menu).
 *
 * Navíc: Solární cyklus — auto přepínání podle elevace slunce
 * (svítání → den → zlatá hodina → nautický soumrak → noc).
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  CloudFog,
  CloudLightning,
  CloudOff,
  CloudRain,
  CloudSnow,
  LocateFixed,
  Palette,
  Sun,
  SunMoon,
} from 'lucide-react';
import {
  applyCycleTheme,
  applyManualTheme,
  CYCLE_SWATCHES,
  isAcmTheme,
  readStoredTheme,
  readThemeMode,
  startThemeCycle,
  THEME_META,
  THEMES,
  themeForNow,
  themeHint,
  type AcmTheme,
  type ThemeMode,
} from '../lib/theme';
import {
  applyManualWeather,
  readWeather,
  readWeatherMode,
  startWeatherAuto,
  WEATHER_META,
  type AcmWeather,
  type WeatherMode,
} from '../lib/weather';
import { api } from '../lib/api';
import { useTranslation } from 'react-i18next';

const WEATHER_CHIPS: readonly {
  id: AcmWeather;
  icon: typeof CloudRain;
}[] = [
  { id: 'off', icon: CloudOff },
  { id: 'rain', icon: CloudRain },
  { id: 'snow', icon: CloudSnow },
  { id: 'sun', icon: Sun },
  { id: 'storm', icon: CloudLightning },
  { id: 'fog', icon: CloudFog },
];

export function persistTheme(theme: AcmTheme, mode: ThemeMode = 'manual'): Promise<void> {
  if (mode === 'cycle') {
    applyCycleTheme();
  } else {
    applyManualTheme(theme);
  }
  return api
    .mcpSession(
      'ui.prefs_set',
      { key: 'theme', value: { theme, mode } },
      { silent: true },
    )
    .then(() => {});
}

function ThemeMini({
  theme,
  active,
  label,
  hint,
  swatches,
}: {
  theme?: AcmTheme;
  active: boolean;
  label: string;
  hint: string;
  swatches: readonly [string, string, string, string];
}) {
  const [a, b, c, d] = swatches;
  return (
    <div
      className={`relative overflow-hidden rounded-xl border transition-all ${
        active
          ? 'border-plex ring-2 ring-[rgb(var(--acm-plex)/0.35)]'
          : 'border-gray-700/80 hover:border-gray-500'
      }`}
    >
      <div
        className='h-12 w-full'
        style={{
          background: `linear-gradient(135deg, ${a} 0%, ${b} 55%, ${c} 100%)`,
        }}
      />
      <div className='flex items-center gap-1.5 px-2.5 py-2 bg-gray-900/80'>
        <div className='flex -space-x-1'>
          {[a, b, c, d].map((hex) => (
            <span
              key={hex}
              className='h-3.5 w-3.5 rounded-full border border-black/20 shadow-sm'
              style={{ backgroundColor: hex }}
              title={hex}
            />
          ))}
        </div>
        <div className='min-w-0 flex-1'>
          <div className='text-[11px] font-semibold text-gray-100 truncate'>{label}</div>
          <div className='text-[9px] text-gray-500 truncate leading-tight'>{hint}</div>
        </div>
        {active && <Check size={12} className='text-plex shrink-0' />}
      </div>
      {theme ? null : (
        <span className='absolute top-1.5 right-1.5 rounded-md bg-black/45 px-1.5 py-0.5 text-[9px] font-medium text-amber-100 backdrop-blur-sm'>
          auto
        </span>
      )}
    </div>
  );
}

type MenuPos = { top: number; left: number; width: number; maxH: number; openUp: boolean };

export function ThemeSwitcher({ compact = false }: { compact?: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPos | null>(null);
  const [theme, setTheme] = useState<AcmTheme>(() =>
    typeof document !== 'undefined' ? readStoredTheme() : themeForNow().theme,
  );
  const [mode, setMode] = useState<ThemeMode>(() =>
    typeof document !== 'undefined' ? readThemeMode() : 'cycle',
  );
  const [phaseLabel, setPhaseLabel] = useState(() =>
    typeof document !== 'undefined' ? themeForNow().phaseLabel : '',
  );
  const [weather, setWeather] = useState<AcmWeather>(() =>
    typeof document !== 'undefined' ? readWeather() : 'off',
  );
  const [weatherMode, setWeatherMode] = useState<WeatherMode>(() =>
    typeof document !== 'undefined' ? readWeatherMode() : 'manual',
  );

  useEffect(() => {
    if (mode === 'cycle') startThemeCycle();
  }, [mode]);

  useEffect(() => {
    if (weatherMode === 'auto') startWeatherAuto();
  }, [weatherMode]);

  useEffect(() => {
    const onWeather = (e: Event) => {
      const d = (e as CustomEvent<{ weather?: AcmWeather; mode?: WeatherMode }>).detail;
      if (d?.weather) setWeather(d.weather);
      if (d?.mode) setWeatherMode(d.mode);
    };
    window.addEventListener('acm-weather', onWeather);
    return () => window.removeEventListener('acm-weather', onWeather);
  }, []);

  useEffect(() => {
    const onCycle = (e: Event) => {
      const d = (e as CustomEvent<{ theme: AcmTheme; phase: string }>).detail;
      if (d?.theme) setTheme(d.theme);
      setMode('cycle');
      setPhaseLabel(themeForNow().phaseLabel);
    };
    window.addEventListener('acm-theme-cycle', onCycle);
    return () => window.removeEventListener('acm-theme-cycle', onCycle);
  }, []);

  const place = () => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    const width = Math.min(320, Math.max(r.width, 280), window.innerWidth - 16);
    const spaceBelow = window.innerHeight - r.bottom - gap;
    const spaceAbove = r.top - gap;
    // Prefer open upward when in bottom chrome (sidebar footer) or not enough room below.
    const openUp = spaceBelow < 340 && spaceAbove > spaceBelow;
    const maxH = Math.min(
      28 * 16,
      Math.max(160, openUp ? spaceAbove - 8 : spaceBelow - 8),
    );
    let left = r.right - width;
    if (left < 8) left = 8;
    if (left + width > window.innerWidth - 8) left = window.innerWidth - 8 - width;
    setPos({
      top: openUp ? r.top - gap : r.bottom + gap,
      left,
      width,
      maxH,
      openUp,
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    place();
    const onScroll = () => place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const switchTheme = (next: AcmTheme) => {
    applyManualTheme(next);
    setMode('manual');
    setTheme(next);
    qc.setQueryData(['ui-theme'], { value: { theme: next, mode: 'manual' } });
    persistTheme(next, 'manual').catch(() => {
      void qc.invalidateQueries({ queryKey: ['ui-theme'] });
    });
    setOpen(false);
  };

  const switchWeather = (next: AcmWeather) => {
    applyManualWeather(next);
    setWeatherMode('manual');
    setWeather(next);
  };

  const switchWeatherAuto = () => {
    setWeatherMode('auto');
    startWeatherAuto();
  };

  const switchCycle = () => {
    const next = applyCycleTheme();
    setMode('cycle');
    setTheme(next);
    const now = themeForNow();
    setPhaseLabel(now.phaseLabel);
    qc.setQueryData(['ui-theme'], { value: { theme: next, mode: 'cycle' } });
    persistTheme(next, 'cycle').catch(() => {
      void qc.invalidateQueries({ queryKey: ['ui-theme'] });
    });
    setOpen(false);
  };

  const meta = THEME_META[theme];
  const WeatherIcon = weatherMode === 'auto'
    ? LocateFixed
    : weather === 'off'
    ? null
    : (WEATHER_CHIPS.find((c) => c.id === weather)?.icon ?? null);
  const triggerLabel = mode === 'cycle' ? t('layout.theme.solar', 'Solár') : meta.label;
  const triggerHint = mode === 'cycle'
    ? `${phaseLabel || themeForNow().phaseLabel} · ${meta.label}`
    : themeHint(theme);

  const menu = open && pos && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={menuRef}
        role='listbox'
        aria-label={t('layout.theme.selectAria', 'Vyber schéma')}
        className='fixed z-[200] flex flex-col rounded-2xl border border-gray-700/80 bg-gray-950/98 p-2 shadow-2xl backdrop-blur-md'
        style={{
          top: pos.openUp ? undefined : pos.top,
          bottom: pos.openUp ? window.innerHeight - pos.top : undefined,
          left: pos.left,
          width: pos.width,
          maxHeight: pos.maxH,
        }}
      >
        <div className='px-1.5 pb-2 pt-0.5 text-[10px] uppercase tracking-wider text-gray-500'>
          {t('nav.appearance', 'Vzhled')}
        </div>
        <div
          className='grid grid-cols-2 gap-2 overflow-y-auto pr-0.5'
          style={{ maxHeight: Math.max(120, pos.maxH - 188) }}
        >
          <button
            type='button'
            role='option'
            aria-selected={mode === 'cycle'}
            onClick={switchCycle}
            className='text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-plex/40 rounded-xl col-span-2'
          >
            <ThemeMini
              active={mode === 'cycle'}
              label={t('layout.theme.solarCycle', 'Solární cyklus')}
              hint={
                mode === 'cycle'
                  ? t('layout.theme.nowPhase', 'Teď: {{phase}} → {{theme}}', {
                    phase: phaseLabel || themeForNow().phaseLabel,
                    theme: meta.label,
                  })
                  : t(
                    'layout.theme.cycleHint',
                    'Auto podle slunce — svítání, den, soumrak, noc',
                  )
              }
              swatches={CYCLE_SWATCHES}
            />
          </button>
          {THEMES.map((id) => (
            <button
              key={id}
              type='button'
              role='option'
              aria-selected={mode === 'manual' && theme === id}
              onClick={() => switchTheme(id)}
              className='text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-plex/40 rounded-xl'
            >
              <ThemeMini
                theme={id}
                active={mode === 'manual' && theme === id}
                label={THEME_META[id].label}
                hint={themeHint(id)}
                swatches={THEME_META[id].swatches}
              />
            </button>
          ))}
        </div>
        <div className='mt-2 border-t border-gray-800/80 px-1 pt-2'>
          <div className='px-0.5 pb-1.5 text-[10px] uppercase tracking-wider text-gray-500'>
            {t('layout.theme.weather', 'Počasí')}
          </div>
          <button
            type='button'
            aria-pressed={weatherMode === 'auto'}
            title={t('layout.theme.weatherAutoHint', 'Nejbližší počasí podle tvé polohy')}
            onClick={switchWeatherAuto}
            className={`mb-1.5 flex w-full items-center gap-1.5 rounded-lg border px-2 py-1.5 text-[10px] font-medium transition-colors ${
              weatherMode === 'auto'
                ? 'border-plex bg-[rgb(var(--acm-plex)/0.14)] text-gray-100'
                : 'border-gray-800/80 text-gray-400 hover:border-gray-600 hover:text-gray-200'
            }`}
          >
            <LocateFixed size={13} className={weatherMode === 'auto' ? 'text-plex' : ''} />
            <span className='truncate'>
              {weatherMode === 'auto'
                ? t('layout.theme.weatherAutoOn', 'Podle polohy · {{weather}}', {
                  weather: t(`layout.theme.weatherLabel.${weather}`, WEATHER_META[weather].label),
                })
                : t('layout.theme.weatherAuto', 'Podle polohy')}
            </span>
          </button>
          <div className='grid grid-cols-3 gap-1' role='group' aria-label={t('layout.theme.weatherAria', 'Filtr počasí')}>
            {WEATHER_CHIPS.map(({ id, icon: Icon }) => {
              const active = weatherMode === 'manual' && weather === id;
              return (
                <button
                  key={id}
                  type='button'
                  title={t(`layout.theme.weatherHint.${id}`, WEATHER_META[id].hint)}
                  aria-pressed={active}
                  onClick={() => switchWeather(id)}
                  className={`flex flex-col items-center gap-0.5 rounded-lg border px-1 py-1.5 text-[9px] font-medium transition-colors ${
                    active
                      ? 'border-plex bg-[rgb(var(--acm-plex)/0.14)] text-gray-100'
                      : 'border-gray-800/80 text-gray-400 hover:border-gray-600 hover:text-gray-200'
                  }`}
                >
                  <Icon size={13} className={active ? 'text-plex' : ''} />
                  <span className='truncate max-w-full'>
                    {t(`layout.theme.weatherLabel.${id}`, WEATHER_META[id].label)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>,
      document.body,
    )
    : null;

  return (
    <div className={`relative inline-flex ${compact ? '' : ''}`}>
      <button
        ref={btnRef}
        type='button'
        onClick={() => setOpen((v) => !v)}
        title={`${triggerLabel} — ${triggerHint}`}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label={t('layout.theme.schemeAria', 'Barevné schéma')}
        className='inline-flex items-center gap-1.5 rounded-lg border border-gray-700/80 bg-gray-900/40 px-2 py-1 text-xs transition-colors hover:border-gray-500 hover:bg-gray-800/50'
      >
        {mode === 'cycle'
          ? <SunMoon size={13} className='text-amber-500 shrink-0' />
          : <Palette size={13} className='text-gray-500 shrink-0' />}
        <span
          className='h-3 w-3 rounded-full border border-black/20 shrink-0'
          style={{ backgroundColor: meta.swatches[2] }}
        />
        <span className='text-gray-200 font-medium tracking-wide'>{triggerLabel}</span>
        {WeatherIcon && <WeatherIcon size={12} className='text-plex shrink-0' />}
        <ChevronDown
          size={12}
          className={`text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {menu}
    </div>
  );
}

/** Načte theme z ui.prefs po loginu (stejný vzor jako jazyk). */
export function useAccountTheme(): void {
  const qc = useQueryClient();
  useEffect(() => {
    let cancelled = false;
    api
      .mcpSession<{ value?: { theme?: string; mode?: string } | null }>('ui.prefs_get', {
        key: 'theme',
      }, {
        silent: true,
      })
      .then((r) => {
        if (cancelled) return;
        const mode = r?.value?.mode;
        const pref = r?.value?.theme === 'cie' ? 'stratosphere' : r?.value?.theme;
        if (mode === 'cycle') {
          const applied = applyCycleTheme();
          qc.setQueryData(['ui-theme'], { value: { theme: applied, mode: 'cycle' } });
          return;
        }
        if (isAcmTheme(pref)) {
          applyManualTheme(pref);
          qc.setQueryData(['ui-theme'], { value: { theme: pref, mode: 'manual' } });
          return;
        }
        // Účet bez volby — solární cyklus (ne night).
        const applied = applyCycleTheme();
        qc.setQueryData(['ui-theme'], { value: { theme: applied, mode: 'cycle' } });
        persistTheme(applied, 'cycle').catch(() => {});
      })
      .catch(() => {
        /* offline / no session — localStorage zůstává */
        if (readThemeMode() === 'cycle') startThemeCycle();
      });
    return () => {
      cancelled = true;
    };
  }, [qc]);
}
